"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Search, Calendar, Folder, Play, Image as ImageIcon, FileText } from "lucide-react";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { FormProvider } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
function MediaArchiveConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    setQuery,
    query,
    refetch
  } = usePosts();
  const [searchTerm, setSearchTerm] = useState(query.search || "");
  const [activeCategory, setActiveCategory] = useState("tutti");
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQuery({
      search: searchTerm
    });
  };
  const handleCategoryChange = (value: string) => {
    if (!value) return;
    setActiveCategory(value);
    if (value === "tutti") {
      setQuery({
        search: ""
      });
      setSearchTerm("");
    } else {
      setQuery({
        search: value
      });
      setSearchTerm(value);
    }
  };
  const renderMediaIcon = (format?: string) => {
    switch (format) {
      case "video":
        return <Play className="size-4" />;
      case "audio":
        return <Play className="size-4" />;
      case "image":
        return <ImageIcon className="size-4" />;
      default:
        return <FileText className="size-4" />;
    }
  };
  return <div className="flex flex-col gap-8">
      {}
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="w-full overflow-x-auto pb-2 md:w-auto md:pb-0">
          <ToggleGroup type="single" value={activeCategory} onValueChange={handleCategoryChange} className="justify-start">
            <ToggleGroupItem value="tutti" aria-label="Tutti i media">
              Tutti
            </ToggleGroupItem>
            <ToggleGroupItem value="Handpan" aria-label="Registrazioni Handpan">
              Handpan
            </ToggleGroupItem>
            <ToggleGroupItem value="Escursioni" aria-label="Documentazione Escursioni">
              Escursioni
            </ToggleGroupItem>
            <ToggleGroupItem value="Guerra" aria-label="Memorie di Guerra">
              Memorie
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <FormProvider>
          <form onSubmit={handleSearch} className="flex w-full max-w-sm items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
              <Input type="search" placeholder="Cerca nell'archivio..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
            <Button type="submit" variant="secondary">
              Cerca
            </Button>
          </form>
        </FormProvider>
      </div>

      {}
      {error ? <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-destructive mb-4">Si è verificato un errore durante il caricamento dell'archivio.</p>
          <Button onClick={refetch} variant="outline">Riprova</Button>
        </div> : loading ? <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border/50 bg-card/50">
              <AspectRatio ratio={16 / 9} className="bg-muted animate-pulse" />
              <CardHeader className="gap-2">
                <div className="h-4 w-1/3 bg-muted animate-pulse rounded" />
                <div className="h-6 w-3/4 bg-muted animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-4 w-full bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>)}
        </div> : posts.length === 0 ? <div className="flex flex-col items-center justify-center py-24 text-center border rounded-xl border-dashed border-border bg-card/30">
          <Folder className="size-12 text-muted-foreground mb-4 opacity-50" />
          <h3 className="font-default text-xl font-semibold mb-2">Nessun elemento trovato</h3>
          <p className="text-muted-foreground max-w-md">
            Non ci sono elementi multimediali che corrispondono ai tuoi criteri di ricerca. Prova a modificare i filtri.
          </p>
          <Button variant="link" onClick={() => handleCategoryChange("tutti")} className="mt-4">
            Mostra tutti gli elementi
          </Button>
        </div> : <>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((post, i) => <article key={post.id} data-index={i} className="group h-full">
                <Link to={post.link || "#"} className="block h-full">
                  <Card className="h-full overflow-hidden transition-all duration-300 hover:shadow-md hover:border-primary/50 flex flex-col">
                    <AspectRatio ratio={16 / 9} className="overflow-hidden bg-muted relative">
                      {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-secondary/20">
                          {renderMediaIcon(post.format)}
                        </div>}
                      <div className="absolute top-3 right-3">
                        <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm shadow-sm">
                          {renderMediaIcon(post.format)}
                          <span className="ml-1 capitalize">{post.format || "Media"}</span>
                        </Badge>
                      </div>
                    </AspectRatio>
                    
                    <CardHeader className="flex-none pb-3">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                        {post.categories && post.categories.length > 0 && <span className="flex items-center gap-1 text-primary">
                            <Folder className="size-3" />
                            {post.categories[0].name}
                          </span>}
                        {post.date && <span className="flex items-center gap-1 ml-auto">
                            <SafeHydrate><Calendar className="size-3" /></SafeHydrate>
                            {new Date(post.date).toLocaleDateString('it-IT', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                          </span>}
                      </div>
                      <CardTitle className="font-default text-xl line-clamp-2 group-hover:text-primary transition-colors">
                        {post.title}
                      </CardTitle>
                    </CardHeader>
                    
                    <CardContent className="flex-grow">
                      {post.excerpt && <p className="text-sm text-muted-foreground line-clamp-3" dangerouslySetInnerHTML={{
                  __html: post.excerpt
                }} />}
                    </CardContent>
                    
                    <CardFooter className="pt-0 mt-auto">
                      <span className="text-sm font-medium text-primary flex items-center gap-1 group-hover:underline underline-offset-4">
                        Esplora <Play className="size-3" />
                      </span>
                    </CardFooter>
                  </Card>
                </Link>
              </article>)}
          </div>

          {}
          {totalPages > 1 && <div className="mt-12">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <Button variant="ghost" size="default" onClick={prevPage} disabled={!hasPrev} className="gap-1 pl-2.5">
                      Precedente
                    </Button>
                  </PaginationItem>
                  
                  <PaginationItem className="hidden sm:flex items-center px-4 text-sm text-muted-foreground">
                    Pagina {currentPage} di {totalPages}
                  </PaginationItem>

                  <PaginationItem>
                    <Button variant="ghost" size="default" onClick={nextPage} disabled={!hasNext} className="gap-1 pr-2.5">
                      Successiva
                    </Button>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>}
        </>}
    </div>;
}
export default function MediaArchive() {
  const wp_query_params = {
    post_type: "post",
    per_page: 9,
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="media-archive" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 md:mb-16 max-w-3xl">
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Archivio Multimediale
          </h2>
          <p className="text-lg text-muted-foreground">
            Esplora la nostra collezione di registrazioni handpan, documentari sulle escursioni e preziose memorie storiche. Utilizza i filtri per trovare i contenuti di tuo interesse.
          </p>
        </div>
        
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 9,
        status: ["publish"]
      }}>
          <MediaArchiveConsumer />
        </PostsProvider>
      </div>
    </section>;
}